import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { z } from 'zod';
import { zodResolver } from '@hookform/resolvers/zod';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui-kit/form';
import { Button } from '@/components/ui-kit/button';
import {
  SharedPasswordStrengthChecker,
  PasswordInput,
  Captcha,
  useCaptcha,
} from '@/components/core';
import { Input } from '@/components/ui-kit/input';
import { LOGIN_COLORS } from '@/modules/auth/constants/login-theme';
import { cn } from '@/lib/utils';

/**
 * BasePasswordForm Component
 *
 * A form component for password creation/reset that includes validation, strength checking,
 * and optional CAPTCHA verification for enhanced security.
 *
 * Features:
 * - Password and confirmation fields with validation
 * - Password strength requirements checker
 * - Optional Google reCAPTCHA integration
 * - Form state management using React Hook Form
 * - Zod schema validation
 * - Loading state handling
 * - Navigation after successful submission
 *
 * Props:
 * @param {string} code - Verification code used for the password operation
 * @param {(password: string, code: string, captchaToken?: string) => Promise<void>} onSubmit - Callback for form submission
 * @param {z.ZodSchema} validationSchema - Zod schema for form validation
 * @param {{password: string, confirmPassword: string}} defaultValues - Default values for the form fields
 * @param {boolean} isPending - Loading state flag for form submission
 * @param {boolean} [isCaptchaValid] - Optional flag indicating if CAPTCHA is valid
 * @param {(isValid: boolean) => void} [onCaptchaValidation] - Optional callback when CAPTCHA validation state changes
 *
 * @example
 * // Basic usage
 * <BasePasswordForm
 *   code="reset123"
 *   onSubmit={handlePasswordReset}
 *   validationSchema={passwordSchema}
 *   defaultValues={{ password: "", confirmPassword: "" }}
 *   isPending={isSubmitting}
 *   onCaptchaValidation={setIsCaptchaValid}
 * />
 */

interface BasePasswordFormProps {
  code: string;
  onSubmit: (
    password: string,
    code: string,
    captchaToken?: string,
    formData?: any
  ) => Promise<void>;
  validationSchema: z.ZodSchema;
  defaultValues: any;
  isPending: boolean;
  isCaptchaValid?: boolean;
  onCaptchaValidation?: (isValid: boolean) => void;
  showNameFields?: boolean;
  theme?: 'default' | 'login';
}

export const BasePasswordForm = ({
  code,
  onSubmit,
  validationSchema,
  defaultValues,
  isPending,
  onCaptchaValidation,
  showNameFields,
  theme = 'default',
}: Readonly<BasePasswordFormProps>) => {
  const navigate = useNavigate();
  const [requirementsMet, setRequirementsMet] = useState(false);
  const [showCaptcha, setShowCaptcha] = useState(false);
  const { t } = useTranslation();

  const googleSiteKey = import.meta.env.VITE_CAPTCHA_SITE_KEY || '';
  const captchaEnabled = googleSiteKey !== '';

  const captchaType =
    import.meta.env.VITE_CAPTCHA_TYPE === 'reCaptcha' ? 'reCaptcha-v2-checkbox' : 'hCaptcha';

  const { code: captchaToken, captcha } = useCaptcha({
    siteKey: googleSiteKey,
    type: captchaType,
  });

  const form = useForm({
    defaultValues,
    resolver: zodResolver(validationSchema),
  });

  const password = form.watch('password');
  const confirmPassword = form.watch('confirmPassword');
  const firstName = form.watch('firstName');
  const lastName = form.watch('lastName');

  useEffect(() => {
    if (
      captchaEnabled &&
      requirementsMet &&
      password &&
      confirmPassword &&
      password === confirmPassword
    ) {
      setShowCaptcha(true);
    } else {
      setShowCaptcha(false);
      if (captchaToken && onCaptchaValidation) {
        onCaptchaValidation(false);
      }
    }
  }, [
    requirementsMet,
    password,
    confirmPassword,
    captchaToken,
    onCaptchaValidation,
    captchaEnabled,
  ]);

  // Sync captcha token with onCaptchaValidation callback
  useEffect(() => {
    if (onCaptchaValidation) {
      onCaptchaValidation(!!captchaToken);
    }
  }, [captchaToken, onCaptchaValidation]);

  const onSubmitHandler = async (values: any) => {
    if (captchaEnabled && !captchaToken) {
      return;
    }

    try {
      await onSubmit(values.password, code, captchaEnabled ? captchaToken : undefined, values);
      navigate('/success');
    } catch (_error) {
      // Handle error if needed
    }
  };

  const nameFieldsValid = !showNameFields || (!!firstName?.trim() && !!lastName?.trim());

  const isSubmitDisabled =
    isPending || !requirementsMet || (captchaEnabled && !captchaToken) || !nameFieldsValid;

  const loginLabelClassName = 'text-sm font-semibold leading-5';
  const loginInputClassName = cn(
    'h-12 rounded-xl border bg-[#FFFFFF] px-4 py-3 text-sm text-[#252728] shadow-none',
    'focus-visible:ring-1 focus-visible:ring-[#0066B2]',
    'placeholder:text-[#A6A8A9]'
  );

  return (
    <Form {...form}>
      <form
        className={cn(
          'flex flex-col',
          theme === 'login' ? 'gap-5' : 'max-h-[75vh] gap-4 overflow-y-auto pr-5'
        )}
        style={
          theme === 'default'
            ? {
                scrollbarWidth: 'thin',
                scrollbarColor: 'hsl(var(--neutral-300)) transparent',
              }
            : undefined
        }
        onSubmit={form.handleSubmit(onSubmitHandler)}
      >
        {showNameFields && (
          <>
            <FormField
              control={form.control}
              name="firstName"
              render={({ field }) => (
                <FormItem className="space-y-2">
                  <FormLabel
                    className={cn(
                      theme === 'login' ? loginLabelClassName : 'font-normal text-high-emphasis'
                    )}
                    style={theme === 'login' ? { color: LOGIN_COLORS.textLabel } : undefined}
                  >
                    {t('FIRST_NAME')}
                  </FormLabel>
                  <FormControl>
                    <Input
                      placeholder={t('ENTER_YOUR_FIRST_NAME')}
                      className={theme === 'login' ? loginInputClassName : undefined}
                      style={theme === 'login' ? { borderColor: LOGIN_COLORS.inputBorder } : undefined}
                      {...field}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="lastName"
              render={({ field }) => (
                <FormItem className="space-y-2">
                  <FormLabel
                    className={cn(
                      theme === 'login' ? loginLabelClassName : 'font-normal text-high-emphasis'
                    )}
                    style={theme === 'login' ? { color: LOGIN_COLORS.textLabel } : undefined}
                  >
                    {t('LAST_NAME')}
                  </FormLabel>
                  <FormControl>
                    <Input
                      placeholder={t('ENTER_YOUR_LAST_NAME')}
                      className={theme === 'login' ? loginInputClassName : undefined}
                      style={theme === 'login' ? { borderColor: LOGIN_COLORS.inputBorder } : undefined}
                      {...field}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          </>
        )}

        <FormField
          control={form.control}
          name="password"
          render={({ field }) => (
            <FormItem className="space-y-2">
              <FormLabel
                className={cn(
                  theme === 'login' ? loginLabelClassName : 'font-normal text-high-emphasis'
                )}
                style={theme === 'login' ? { color: LOGIN_COLORS.textLabel } : undefined}
              >
                {t('PASSWORD')}
              </FormLabel>
              <FormControl>
                <PasswordInput
                  placeholder={t('ENTER_YOUR_PASSWORD')}
                  className={theme === 'login' ? loginInputClassName : undefined}
                  style={theme === 'login' ? { borderColor: LOGIN_COLORS.inputBorder } : undefined}
                  {...field}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="confirmPassword"
          render={({ field }) => (
            <FormItem className="space-y-2">
              <FormLabel
                className={cn(
                  theme === 'login' ? loginLabelClassName : 'font-normal text-high-emphasis'
                )}
                style={theme === 'login' ? { color: LOGIN_COLORS.textLabel } : undefined}
              >
                {t('CONFIRM_PASSWORD')}
              </FormLabel>
              <FormControl>
                <PasswordInput
                  placeholder={t('CONFIRM_YOUR_PASSWORD')}
                  className={theme === 'login' ? loginInputClassName : undefined}
                  style={theme === 'login' ? { borderColor: LOGIN_COLORS.inputBorder } : undefined}
                  {...field}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <SharedPasswordStrengthChecker
          password={password}
          confirmPassword={confirmPassword}
          onRequirementsMet={setRequirementsMet}
        />

        {captchaEnabled && showCaptcha && (
          <div className="my-4">
            <Captcha {...captcha} theme="light" size="normal" />
          </div>
        )}

        <div className={cn('flex gap-10', theme === 'login' ? 'mt-2' : 'mt-5')}>
          <Button
            className={cn(
              'flex-1 font-extrabold',
              theme === 'login' &&
                'h-12 rounded-xl border-0 text-sm font-semibold shadow-none hover:opacity-90'
            )}
            size="lg"
            type="submit"
            loading={isPending}
            disabled={isSubmitDisabled}
            style={
              theme === 'login'
                ? { backgroundColor: LOGIN_COLORS.primary, color: LOGIN_COLORS.pureWhite }
                : undefined
            }
          >
            {t('CONFIRM')}
          </Button>
        </div>
      </form>
    </Form>
  );
};
